import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { Input } from "components/ui/input";
import PhoneNumberInput from "components/ui/phone-input";
import {
  PersonalData,
  PersonalDataValidation,
  SomeoneElseData,
  SomeoneElseDataValidation,
} from "../../../../../types/bookingCreation";
import {
  addressChanged,
  agreementChanged,
  cityChanged,
  companyNameChanged,
  countryChanged,
  emailChanged,
  firstNameChanged,
  lastNameChanged,
  coveredBookingChanged,
  personalDataRevalidated,
  phoneChanged,
  setForSomeoneElse,
  someoneElseEmailChanged,
  someoneElseFirstNameChanged,
  someoneElseLastNameChanged,
  uscCustomerIdChanged,
  zipCodeChanged,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { RootState } from "../../../../../redux/store";
import { usePersonalDataPersistence } from "../../hooks/usePersonalDataPersistence";
import { useEmailFieldFeedback } from "../../hooks/useEmailFieldFeedback";
import CountryCombobox from "./CountryCombobox";
import AccountLoginPrompt from "../../../../account/components/AccountLoginPrompt";
import HintsDialog from "../../../../hints/components/HintsDialog";
import { useInPlaceTerms } from "../../hooks/useTenantTermsPage";
import { useIsCoverageEligible, useIsCoveredBooking } from "../../hooks/useCoveredBooking";
import CoveringOrganizationSelect from "../BookingSummary/CoveringOrganizationSelect";
import {
  PersonalDataFieldKey,
  requiredPersonalDataFields,
} from "../../../../../util/requiredPersonalDataFields";

/**
 * PersonalDataStepShared (issue #868)
 * ---------------------------------------------------------------------------
 * Single, prop-gated customer-data form that consolidates the previously
 * duplicated per-flow `PersonalDataStep.tsx` files. Built on the ShadCN/Tailwind
 * stack (modelled on the already-migrated `sup` flow) — the end-user app never
 * adds new MUI code.
 *
 * Field *visibility* and *mandatory* state are driven entirely by props (never
 * hardcoded internals) so the same component can later be driven by tenant
 * configuration per use case (Ticket B / #1401, admin home per ADR-0005)
 * without a rewrite. The USC customer-id field stays gated on the runtime
 * `partner === "usc"` signal (identical across every flow), so it is not a prop.
 */

/**
 * Re-exported for the flows that already import it from here. The type now
 * lives beside the rule that uses it (`util/requiredPersonalDataFields.ts`),
 * so the form and the Redux gate cannot end up with different ideas of what a
 * field key is.
 */
export type { PersonalDataFieldKey };

export interface PersonalDataStepSharedProps {
  /** Render the (optional) company name field. */
  showCompany?: boolean;
  /** Render the address block: address + country + city + zip. */
  showAddress?: boolean;
  /** Render the "booking for myself / someone else" radio group. */
  showBookingForOptions?: boolean;
  /**
   * Flows that only display an agreement *notice* (no explicit checkbox) mark
   * the booking as agreed on mount. When true, `agreementChanged({ isAgreed:
   * true })` is dispatched once and `onAgreementChange` is notified.
   */
  showAgreement?: boolean;
  /** Observe/react to the agreement state set by `showAgreement`. */
  onAgreementChange?: (isAgreed: boolean) => void;
  /**
   * Target of the terms-and-conditions link. Defaults to the generic `/hints`
   * index; flows with a tenant-specific terms page pass a resolved path (see
   * `useDataProtectionPath`).
   */
  dataProtectionPath?: string;
  /**
   * Offer "already have an account? log in" above the form (#1926). Opt-in per
   * flow, like every other element here — the component is prop-gated by
   * design, and seven flows render it. Only the venue flow asks for it today,
   * because that is where an account changes the booking outcome.
   */
  showAccountLogin?: boolean;
  /**
   * Set of fields rendered as mandatory. Defaults to the standard booking set
   * (everything except company). The address fields are additionally gated by
   * `showAddress`; USC by the runtime partner signal.
   */
  requiredFields?: ReadonlySet<PersonalDataFieldKey>;
}

type TouchableField =
  | PersonalDataFieldKey
  | "someoneElseFirstName"
  | "someoneElseLastName"
  | "someoneElseEmail";

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  required: boolean;
  /** Show the invalid state (typically `touched && !valid`). */
  invalid: boolean;
  autoComplete?: string;
  type?: string;
  /** Optional inline error message shown when `invalid`. */
  errorMessage?: string;
}

/** Label + ShadCN Input + optional inline error, matching the `sup` flow. */
const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  required,
  invalid,
  autoComplete,
  type = "text",
  errorMessage,
}) => {
  const errorId = `${id}-error`;
  const showError = invalid && !!errorMessage;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-900">
        {label}
        {required && (
          <span aria-hidden="true" className="text-appRed ml-0.5">
            *
          </span>
        )}
      </label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        /* Normalise HERE, when the customer leaves the field — never while
           they are typing. The reducers used to trim on every keystroke, and
           because these inputs are controlled that made a space impossible to
           enter: "kolula Solutions" came out "kolulaSolutions" (#2948).
           One place, because every text field on this step is a `TextField`. */
        onBlur={() => {
          const normalized = value.trim();
          if (normalized !== value) onChange(normalized);
          onBlur();
        }}
        autoComplete={autoComplete}
        required={required}
        aria-required={required || undefined}
        error={invalid}
        aria-invalid={invalid || undefined}
        aria-describedby={showError ? errorId : undefined}
      />
      {showError && (
        <p id={errorId} className="text-sm text-appRed mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

const PersonalDataStepShared: React.FC<PersonalDataStepSharedProps> = ({
  showCompany = false,
  showAddress = false,
  showBookingForOptions = false,
  showAgreement = false,
  showAccountLogin = false,
  onAgreementChange,
  dataProtectionPath = "/hints",
  requiredFields: requiredFieldsProp,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isCovered = useIsCoveredBooking();
  const coverageEligible = useIsCoverageEligible();
  const useCase = useSelector(
    (state: RootState) => state.bookingCreation.useCase
  );

  /**
   * The rendered required set. Resolved from the SAME definition the Redux gate
   * checks (`util/requiredPersonalDataFields.ts`) rather than from a local
   * constant — previously the two disagreed, and the customer saw an optional
   * address field above a Next button that demanded one.
   *
   * `uscCustomerId` is added here and not there because it is always required
   * when it is shown at all (the runtime `partner === "usc"` signal decides
   * that), so it is not a field the required set can vary.
   *
   * An explicit prop still wins, for a caller that genuinely knows better.
   */
  const requiredFields = useMemo(
    () =>
      requiredFieldsProp ??
      new Set<PersonalDataFieldKey>([
        ...Array.from(requiredPersonalDataFields(useCase, { covered: isCovered })),
        "uscCustomerId",
      ]),
    [requiredFieldsProp, useCase, isCovered]
  );
  usePersonalDataPersistence();

  // Terms are shown IN PLACE, without leaving the booking route.
  //
  // The link used to navigate to `/hints...`. That unmounts the flow's route,
  // and the flow pages reset the wizard to their first step on mount — so
  // closing the terms, or pressing the browser's back button, dropped the
  // customer back at the start of the booking. Reading the T&Cs threw you out
  // of the flow, which for a legally required link is the worst possible place
  // for that to happen.
  //
  // The open state lives in a search param (see `useInPlaceTerms`), so the
  // browser's back button closes the sheet instead of leaving the flow.
  const { TermsPage, termsOpen, openTerms, closeTerms } = useInPlaceTerms();

  const personalData: PersonalData = useSelector(
    (state: RootState) => state.bookingCreation.personalData
  );
  const personalDataValidation: PersonalDataValidation = useSelector(
    (state: RootState) => state.bookingCreation.personalDataValidation
  );
  const someoneElseData: SomeoneElseData = useSelector(
    (state: RootState) => state.bookingCreation.someoneElseData
  );
  const someoneElseDataValidation: SomeoneElseDataValidation = useSelector(
    (state: RootState) => state.bookingCreation.someoneElseDataValidation
  );
  const partner = useSelector(
    (state: RootState) => state.bookingCreation.partner
  );
  const isPartnerUsc = partner === "usc";

  const isRequired = (field: PersonalDataFieldKey) => requiredFields.has(field);

  const [touched, setTouched] = useState<Partial<Record<TouchableField, boolean>>>(
    {}
  );
  const markTouched = (field: TouchableField) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const emailFeedback = useEmailFieldFeedback(
    personalData.email,
    !!touched.email
  );

  // Read from the slice, not local state: the step remounts on every entry,
  // and a local default of "myself" showed "myself" on the way back while the
  // booking still counted as made for someone else.
  const isForSomeoneElse = useSelector(
    (state: RootState) => state.bookingCreation.isForSomeoneElse
  );
  const bookingFor: "myself" | "someone_else" = isForSomeoneElse
    ? "someone_else"
    : "myself";
  const handleBookingFor = (selected: "myself" | "someone_else") => {
    dispatch(setForSomeoneElse({ isForSomeoneElse: selected !== "myself" }));
  };

  // Mirror the covered-booking fact into the booking slice, which cannot read
  // the apartment slice itself (#1926). Its own effect, not folded into the
  // mount effect below, because coverage can change AFTER mount — the
  // availability response that carries `coveredForMe` may still be in flight
  // when this step renders, and the required field set has to follow it.
  // Separate init and reaction, per CLAUDE.md.
  useEffect(() => {
    dispatch(coveredBookingChanged({ covered: isCovered }));
  }, [dispatch, isCovered]);

  useEffect(() => {
    if (showAgreement) {
      dispatch(agreementChanged({ isAgreed: true }));
      onAgreementChange?.(true);
    }
    // Re-derive validation + nextEnabled from the data present on step entry.
    // Guards two cases: flows whose own step slices don't recompute
    // nextEnabled when the step changes, and persisted drafts saved before a
    // validation rule existed (they must re-validate, not sail through).
    dispatch(personalDataRevalidated());
    // Run once on mount, mirroring the legacy per-flow effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookingForOptions = useMemo(
    () => ["myself", "someone_else"] as const,
    []
  );

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full max-w-md mx-auto space-y-4 pb-4 px-2">
        {/* An offer, never a gate — guest checkout stays an equal path. */}
        {showAccountLogin && <AccountLoginPrompt />}
        {/* Who pays — before the fields, because "Privat buchen" asks for
            the full set a covered booking does not need. */}
        {coverageEligible && <CoveringOrganizationSelect />}
        <TextField
          id="pd-first-name"
          label={t("book_location.personal_data.first_name")}
          value={personalData.firstName}
          onChange={(v) => dispatch(firstNameChanged({ firstName: v }))}
          onBlur={() => markTouched("firstName")}
          required={isRequired("firstName")}
          invalid={!!touched.firstName && !personalDataValidation.firstName}
          autoComplete="given-name"
          errorMessage={
            personalData.firstName.trim()
              ? t("book_location.personal_data.validation.first_name_invalid", {
                  defaultValue: "Bitte gib deinen echten Vornamen ein.",
                })
              : t("book_location.personal_data.validation.first_name_required", {
                  defaultValue: "Bitte gib deinen Vornamen ein.",
                })
          }
        />

        <TextField
          id="pd-last-name"
          label={t("book_location.personal_data.last_name")}
          value={personalData.lastName}
          onChange={(v) => dispatch(lastNameChanged({ lastName: v }))}
          onBlur={() => markTouched("lastName")}
          required={isRequired("lastName")}
          invalid={!!touched.lastName && !personalDataValidation.lastName}
          autoComplete="family-name"
          errorMessage={
            personalData.lastName.trim()
              ? t("book_location.personal_data.validation.last_name_invalid", {
                  defaultValue: "Bitte gib deinen echten Nachnamen ein.",
                })
              : t("book_location.personal_data.validation.last_name_required", {
                  defaultValue: "Bitte gib deinen Nachnamen ein.",
                })
          }
        />

        {/* Email (with typo-suggestion / soft-warning feedback).
            Email is rendered before phone in every flow (product decision). */}
        <div className="space-y-1">
          <label
            htmlFor="pd-email"
            className="text-sm font-medium text-slate-900"
          >
            {t("book_location.personal_data.email")}
            {isRequired("email") && (
              <span aria-hidden="true" className="text-appRed ml-0.5">
                *
              </span>
            )}
          </label>
          <Input
            id="pd-email"
            type="email"
            value={personalData.email}
            onChange={(e) => dispatch(emailChanged({ email: e.target.value }))}
            onBlur={() => markTouched("email")}
            autoComplete="email"
            required={isRequired("email")}
            aria-required={isRequired("email") || undefined}
            error={!!touched.email && !personalDataValidation.email}
            aria-invalid={!!touched.email && !personalDataValidation.email}
            aria-describedby={
              emailFeedback.error
                ? "pd-email-error"
                : emailFeedback.warning
                ? "pd-email-warning"
                : undefined
            }
          />
          {emailFeedback.error && (
            <p id="pd-email-error" className="text-sm text-appRed mt-1">
              {emailFeedback.error}
            </p>
          )}
          {emailFeedback.warning && (
            <p id="pd-email-warning" className="text-sm text-slate-600 mt-1">
              {emailFeedback.warning}
            </p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-1">
          <label
            htmlFor="pd-phone"
            className="text-sm font-medium text-slate-900"
          >
            {t("book_location.personal_data.phone_number")}
            {isRequired("phone") && (
              <span aria-hidden="true" className="text-appRed ml-0.5">
                *
              </span>
            )}
          </label>
          <PhoneNumberInput
            id="pd-phone"
            value={personalData.phone}
            onChange={(val) => dispatch(phoneChanged({ phone: val }))}
            onBlur={() => markTouched("phone")}
            autoComplete="tel"
            error={!!touched.phone && !personalDataValidation.phone}
            aria-invalid={!!touched.phone && !personalDataValidation.phone}
            aria-describedby={
              !!touched.phone && !personalDataValidation.phone
                ? "pd-phone-error"
                : undefined
            }
          />
          {!!touched.phone && !personalDataValidation.phone && (
            <p id="pd-phone-error" className="text-sm text-appRed mt-1">
              {t("book_location.personal_data.validation.phone_invalid", {
                defaultValue: "Bitte gib eine gültige Telefonnummer ein.",
              })}
            </p>
          )}
        </div>

        {showCompany && (
          <TextField
            id="pd-company"
            label={t("book_location.personal_data.company_name")}
            value={personalData.company ?? ""}
            onChange={(v) => dispatch(companyNameChanged({ company: v }))}
            onBlur={() => markTouched("company")}
            required={isRequired("company")}
            invalid={false}
            autoComplete="organization"
          />
        )}

        {showAddress && (
          <>
            <TextField
              id="pd-address"
              label={t("book_location.personal_data.address")}
              value={personalData.address ?? ""}
              onChange={(v) => dispatch(addressChanged({ address: v }))}
              onBlur={() => markTouched("address")}
              required={isRequired("address")}
              invalid={!!touched.address && !personalDataValidation.address}
              autoComplete="street-address"
            />
            <CountryCombobox
              id="pd-country"
              label={t("book_location.personal_data.country")}
              value={personalData.country ?? ""}
              onChange={(country) => dispatch(countryChanged({ country }))}
              onBlur={() => markTouched("country")}
              required={isRequired("country")}
              error={!!touched.country && !personalDataValidation.country}
            />
            <TextField
              id="pd-city"
              label={t("book_location.personal_data.city")}
              value={personalData.city ?? ""}
              onChange={(v) => dispatch(cityChanged({ city: v }))}
              onBlur={() => markTouched("city")}
              required={isRequired("city")}
              invalid={!!touched.city && !personalDataValidation.city}
              autoComplete="address-level2"
            />
            <TextField
              id="pd-zip"
              label={t("book_location.personal_data.zip_code")}
              value={personalData.zipCode ?? ""}
              onChange={(v) => dispatch(zipCodeChanged({ zipCode: v }))}
              onBlur={() => markTouched("zipCode")}
              required={isRequired("zipCode")}
              invalid={!!touched.zipCode && !personalDataValidation.zipCode}
              autoComplete="postal-code"
            />
          </>
        )}

        {showBookingForOptions && (
          <fieldset className="w-full">
            <legend className="text-sm font-semibold text-slate-900 mb-1">
              {t("book_location.personal_data.booking_for")}
            </legend>
            <div role="radiogroup" className="flex gap-6">
              {bookingForOptions.map((option) => (
                <label
                  key={option}
                  className="flex items-center gap-2 text-sm text-slate-900"
                >
                  <input
                    type="radio"
                    name="pd-booking-for"
                    value={option}
                    checked={bookingFor === option}
                    onChange={() => handleBookingFor(option)}
                    className="h-4 w-4 accent-primary"
                  />
                  {t(`book_location.personal_data.${option}`)}
                </label>
              ))}
            </div>

            {bookingFor === "someone_else" && (
              <div className="space-y-4 mt-4">
                <TextField
                  id="pd-someone-else-first-name"
                  label={t("book_location.personal_data.first_name")}
                  value={someoneElseData.firstName}
                  onChange={(v) =>
                    dispatch(someoneElseFirstNameChanged({ firstName: v }))
                  }
                  onBlur={() => markTouched("someoneElseFirstName")}
                  required
                  invalid={
                    !!touched.someoneElseFirstName &&
                    !someoneElseDataValidation.firstName
                  }
                  autoComplete="off"
                />
                <TextField
                  id="pd-someone-else-last-name"
                  label={t("book_location.personal_data.last_name")}
                  value={someoneElseData.lastName}
                  onChange={(v) =>
                    dispatch(someoneElseLastNameChanged({ lastName: v }))
                  }
                  onBlur={() => markTouched("someoneElseLastName")}
                  required
                  invalid={
                    !!touched.someoneElseLastName &&
                    !someoneElseDataValidation.lastName
                  }
                  autoComplete="off"
                />
                <TextField
                  id="pd-someone-else-email"
                  label={t("book_location.personal_data.email")}
                  type="email"
                  value={someoneElseData.email}
                  onChange={(v) =>
                    dispatch(someoneElseEmailChanged({ email: v }))
                  }
                  onBlur={() => markTouched("someoneElseEmail")}
                  required
                  invalid={
                    !!touched.someoneElseEmail &&
                    !someoneElseDataValidation.email
                  }
                  autoComplete="email"
                />
              </div>
            )}
          </fieldset>
        )}

        {isPartnerUsc && (
          <TextField
            id="pd-usc-customer-id"
            label={t("book_location.personal_data.usc_customer_id")}
            value={personalData.uscCustomerId ?? ""}
            onChange={(v) =>
              dispatch(uscCustomerIdChanged({ uscCustomerId: v }))
            }
            onBlur={() => markTouched("uscCustomerId")}
            required={isRequired("uscCustomerId")}
            invalid={
              !!touched.uscCustomerId && !personalDataValidation.uscCustomerId
            }
            errorMessage={t(
              "book_location.personal_data.validation.usc_customer_id_required",
              { defaultValue: "Bitte gib deine USC-Kundennummer ein." }
            )}
          />
        )}

        <p className="text-sm text-slate-900 pt-2">
          <Trans
            i18nKey="book_location.personal_data.terms_and_conditions"
            components={{
              termsAndConditionLink: TermsPage ? (
                <button
                  type="button"
                  onClick={openTerms}
                  className="underline text-left"
                />
              ) : (
                // Legacy tenants have no component-based terms page; their hint
                // pages come from the backend list, so they keep the navigation
                // they have always had.
                <RouterLink to={dataProtectionPath} className="underline" />
              ),
            }}
          />
        </p>
      </div>

      {TermsPage && (
        <HintsDialog
          open={termsOpen}
          onClose={closeTerms}
          showFaqsBack={false}
        >
          <TermsPage lang={i18n.language} />
        </HintsDialog>
      )}
    </div>
  );
};

export default PersonalDataStepShared;
